import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  searchComplexClaims,
  getComplexClaimEmails,
  ComplexClaim,
  ComplexEmail,
  ComplexClaimsSearchParams,
} from '../api/complexClaims';

const PAGE_LIMIT = 50;

const formatDateTime = (value: string | null): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString('en-GB');
};

const fullName = (c: ComplexClaim): string =>
  [c.firstName, c.lastName].filter(Boolean).join(' ') || '—';

const inputStyle: React.CSSProperties = {
  padding: '8px 12px',
  border: '1px solid #ccc',
  borderRadius: 6,
  fontSize: 14,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: '#5f6368',
  marginBottom: 4,
};

const DOC_LINKS: { key: keyof ComplexClaim; label: string }[] = [
  { key: 'formOfAuthorityUrl', label: 'Form of Authority' },
  { key: 'damageBasedAgreementUrl', label: 'Damage-Based Agreement' },
  { key: 'locDocUrl', label: 'Letter of Complaint' },
  { key: 'clientIdDocUrl', label: 'Client ID' },
  { key: 'signatureAuditTrailUrl', label: 'Signature Audit Trail' },
];

/** The legacy snapshot stores each email's content as a URL to the hosted
 * HTML file on S3 (verified: all 298,770 rows), so viewing is a plain link. */
const isContentUrl = (content: string | null): content is string =>
  !!content && /^https?:\/\//i.test(content);

const ComplexClaimsPage: React.FC = () => {
  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [items, setItems] = useState<ComplexClaim[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<ComplexClaim | null>(null);
  const [drawerIn, setDrawerIn] = useState(false);
  const [emails, setEmails] = useState<ComplexEmail[]>([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [emailsError, setEmailsError] = useState<string | null>(null);
  const [expandedEmailId, setExpandedEmailId] = useState<string | null>(null);

  // Animate the drawer in on open (the shared .claim-details-drawer CSS drives the slide).
  useEffect(() => {
    if (!selected) {
      setDrawerIn(false);
      return;
    }
    const frame = requestAnimationFrame(() => setDrawerIn(true));
    return () => cancelAnimationFrame(frame);
  }, [selected]);

  const closeDrawer = () => {
    setDrawerIn(false);
    setTimeout(() => setSelected(null), 300); // match the CSS slide duration
  };

  // Guards against stale-response races: if the user submits a new search or
  // pages quickly, an older in-flight request may resolve after a newer one.
  // Only the latest request (by id) is allowed to write to state.
  const searchRequestId = useRef(0);

  const runSearch = useCallback(async (q: string, targetPage: number) => {
    const requestId = ++searchRequestId.current;
    setLoading(true);
    setError(null);
    const params: ComplexClaimsSearchParams = { page: targetPage, limit: PAGE_LIMIT };
    if (q.trim()) params.q = q.trim();
    try {
      const response = await searchComplexClaims(params);
      if (requestId !== searchRequestId.current) return; // a newer search superseded this one
      setItems(response.items);
      setTotal(response.total);
      setPage(response.page);
    } catch (err: any) {
      if (requestId !== searchRequestId.current) return;
      setError(err?.response?.data?.error || err?.message || 'Failed to search complex claims');
      setItems([]);
      setTotal(0);
    } finally {
      if (requestId === searchRequestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    runSearch('', 1);
  }, [runSearch]);

  // Load the selected claim's linked emails.
  useEffect(() => {
    if (!selected) {
      setEmails([]);
      setEmailsError(null);
      setExpandedEmailId(null);
      return;
    }
    let cancelled = false;
    setEmailsLoading(true);
    setEmailsError(null);
    setExpandedEmailId(null);
    getComplexClaimEmails(selected.claimID)
      .then(list => {
        if (!cancelled) setEmails(list);
      })
      .catch(err => {
        if (!cancelled) {
          setEmails([]);
          setEmailsError(err?.response?.data?.error || err?.message || 'Failed to load emails');
        }
      })
      .finally(() => {
        if (!cancelled) setEmailsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selected]);

  const handleSearch = () => {
    setAppliedQuery(query);
    runSearch(query, 1);
  };

  const handleClear = () => {
    setQuery('');
    setAppliedQuery('');
    runSearch('', 1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch();
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

  const goToPage = (targetPage: number) => {
    if (targetPage < 1 || targetPage > totalPages) return;
    runSearch(appliedQuery, targetPage);
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 24, color: '#202124' }}>FC Search</h1>
        <p style={{ margin: '4px 0 0', color: '#5f6368', fontSize: 14 }}>
          Search the legacy complex / FC-FM claims snapshot by email or name, and view the
          emails linked to each claim.
        </p>
      </div>

      {/* Search */}
      <div
        style={{
          background: 'white',
          padding: 20,
          borderRadius: 8,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: 20,
        }}
      >
        <label style={labelStyle}>Email or name</label>
        <div style={{ display: 'flex', gap: 12 }}>
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g. jane@example.com or Smith"
            style={{ ...inputStyle, flex: 1 }}
          />
          <button
            onClick={handleSearch}
            disabled={loading}
            style={{
              padding: '8px 20px',
              background: 'linear-gradient(135deg, #01B8C0 0%, #004754 100%)',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 600,
              cursor: loading ? 'default' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            Search
          </button>
          <button
            onClick={handleClear}
            disabled={loading}
            style={{
              padding: '8px 20px',
              background: 'white',
              color: '#5f6368',
              border: '1px solid #ccc',
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 600,
              cursor: loading ? 'default' : 'pointer',
            }}
          >
            Clear
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 12, color: '#5f6368', fontSize: 14 }}>
        {loading ? 'Loading…' : `${total} claims`}
      </div>

      {error && (
        <div
          style={{
            background: '#fce8e6',
            color: '#d93025',
            padding: '12px 16px',
            borderRadius: 6,
            marginBottom: 16,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {/* Results table */}
      <div
        style={{
          background: 'white',
          borderRadius: 8,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f8f9fa', textAlign: 'left' }}>
                <th style={thStyle}>Claim ID</th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Postcode</th>
                <th style={thStyle}>Lender</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#5f6368' }}>
                    No claims found
                  </td>
                </tr>
              )}
              {items.map(item => (
                <tr
                  key={item.claimID}
                  onClick={() => setSelected(item)}
                  style={{
                    borderTop: '1px solid #eee',
                    cursor: 'pointer',
                    background: selected?.claimID === item.claimID ? '#e6fbfc' : 'white',
                  }}
                >
                  <td style={tdStyle}>{item.claimID}</td>
                  <td style={tdStyle}>{fullName(item)}</td>
                  <td style={tdStyle}>{item.email || '—'}</td>
                  <td style={tdStyle}>{item.postcode || '—'}</td>
                  <td style={tdStyle}>{item.craFinanceLender || '—'}</td>
                  <td style={tdStyle}>{item.status || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          marginTop: 16,
        }}
      >
        <button
          onClick={() => goToPage(page - 1)}
          disabled={loading || page <= 1}
          style={pagerStyle(loading || page <= 1)}
        >
          Prev
        </button>
        <span style={{ color: '#5f6368', fontSize: 14 }}>
          Page {page} of {totalPages}
        </span>
        <button
          onClick={() => goToPage(page + 1)}
          disabled={loading || page >= totalPages}
          style={pagerStyle(loading || page >= totalPages)}
        >
          Next
        </button>
      </div>

      {/* Detail slide-over (shared claims-hub drawer styling from ClaimsTable.css) */}
      {selected && (
        <>
          <div
            onClick={closeDrawer}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.3)',
              zIndex: 9998,
              opacity: drawerIn ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
            }}
          />
          <div className={`claim-details-drawer${drawerIn ? ' visible' : ''}`}>
            <div className="details-drawer-header">
              <div>
                <h3 style={{ margin: 0 }}>{fullName(selected)}</h3>
                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span className="unit-badge">{selected.claimID}</span>
                  {selected.status && <span className="unit-badge">Status: {selected.status}</span>}
                  {selected.craFinanceLender && (
                    <span className="unit-badge">{selected.craFinanceLender}</span>
                  )}
                </div>
              </div>
              <div className="header-actions">
                <button
                  className="close-drawer-button"
                  onClick={closeDrawer}
                  aria-label="Close detail"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="details-drawer-content">
            <div className="info-section">
            <h4 style={sectionH4}>Client Information</h4>
            <div style={cardStyle}>
              <dl style={{ margin: 0, fontSize: 14 }}>
                <DetailRow label="Name" value={fullName(selected)} />
                <DetailRow label="Email" value={selected.email} />
                <DetailRow label="Phone" value={selected.phoneNumber} />
                <DetailRow label="Date of birth" value={selected.dateOfBirth} />
                <DetailRow
                  label="Address"
                  value={[selected.addressLine1, selected.addressLine2, selected.city, selected.postcode]
                    .filter(Boolean)
                    .join(', ') || null}
                />
                <DetailRow label="Known names" value={selected.previouslyKnownNames} />
                <DetailRow label="Client ID" value={selected.clientID ? <Mono>{selected.clientID}</Mono> : null} />
              </dl>
            </div>
            </div>

            <div className="info-section">
            <h4 style={sectionH4}>Credit Agreement</h4>
            <div style={cardStyle}>
              <dl style={{ margin: 0, fontSize: 14 }}>
                <DetailRow label="Lender" value={selected.craFinanceLender} />
                <DetailRow label="Agreement no." value={selected.agreementNumber} />
                <DetailRow label="CRA agreement" value={selected.craAgreementNumberOriginal} />
                <DetailRow label="Agreement type" value={selected.craAgreementType} />
                <DetailRow label="Loan amount" value={selected.craOriginalLoanAmount} />
                <DetailRow label="Current balance" value={selected.craCurrentBalance} />
                <DetailRow label="Term" value={selected.craTerm} />
                <DetailRow label="Monthly payment" value={selected.craMonthlyPayment} />
                <DetailRow label="Claim start" value={selected.claimStartDate} />
                <DetailRow label="Status" value={selected.status} />
              </dl>
            </div>
            </div>

            <div className="info-section">
            <h4 style={sectionH4}>Files & Documents</h4>
            <div style={{ margin: '0 0 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {DOC_LINKS.filter(d => selected[d.key]).length === 0 ? (
                <p style={{ color: '#9aa0a6', fontSize: 14, margin: 0 }}>No documents recorded.</p>
              ) : (
                DOC_LINKS.filter(d => selected[d.key]).map(d => {
                  const url = selected[d.key];
                  // Same http(s) guard as email content: only render an anchor for a
                  // valid http(s) URL; otherwise show non-clickable text (blocks
                  // javascript:/data: schemes).
                  return isContentUrl(url) ? (
                    <a
                      key={d.key}
                      className="view-document-link"
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 14 }}
                    >
                      📄 {d.label} ↗
                    </a>
                  ) : (
                    <span key={d.key} style={{ color: '#9aa0a6', fontSize: 14 }}>
                      📄 {d.label} (unavailable)
                    </span>
                  );
                })
              )}
            </div>
            </div>

            <div className="info-section">
            <h4 style={sectionH4}>
              Linked Emails {emailsLoading ? '' : `(${emails.length})`}
            </h4>
            {emailsLoading ? (
              <p style={{ color: '#9aa0a6', fontSize: 14 }}>Loading emails…</p>
            ) : emailsError ? (
              <p style={{ color: '#d93025', fontSize: 14 }}>{emailsError}</p>
            ) : emails.length === 0 ? (
              <p style={{ color: '#9aa0a6', fontSize: 14 }}>No emails linked to this claim.</p>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {emails.map(email => {
                  const open = expandedEmailId === email.id;
                  return (
                    <li
                      key={email.id}
                      style={{
                        border: open ? '1px solid #01B8C0' : '1px solid #e5e7eb',
                        borderRadius: 8,
                        marginBottom: 10,
                        overflow: 'hidden',
                        background: '#f9fafb',
                      }}
                    >
                      <div
                        onClick={() => setExpandedEmailId(open ? null : email.id)}
                        style={{
                          padding: '12px 14px',
                          cursor: 'pointer',
                          background: open ? '#e6fbfc' : '#f9fafb',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: '#202124', fontSize: 14 }}>
                            {email.subject || '(no subject)'}
                          </div>
                          <div style={{ color: '#5f6368', fontSize: 12, marginTop: 4 }}>
                            {formatDateTime(email.sent_at || email.received_at || email.created_at)}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                          {email.type && (
                            <span
                              style={{
                                background: '#e5e7eb',
                                color: '#374151',
                                borderRadius: 999,
                                padding: '2px 10px',
                                fontSize: 11,
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {email.type}
                            </span>
                          )}
                          <span style={{ color: '#01B8C0', fontSize: 12 }}>{open ? '▲' : '▼'}</span>
                        </div>
                      </div>
                      {open && (
                        <div
                          style={{
                            padding: '12px 14px',
                            borderTop: '1px solid #e5e7eb',
                            fontSize: 13,
                            background: 'white',
                          }}
                        >
                          <DetailRow label="To" value={email.to} />
                          <DetailRow label="From" value={email.from} />
                          <DetailRow label="Sent" value={formatDateTime(email.sent_at)} />
                          <DetailRow label="Received" value={formatDateTime(email.received_at)} />
                          <DetailRow label="Delivered" value={formatDateTime(email.delivered_at)} />
                          <DetailRow label="Opened" value={formatDateTime(email.opened_at)} />
                          <DetailRow label="Bounced" value={formatDateTime(email.bounced_at)} />
                          <DetailRow
                            label="Message ID"
                            value={email.message_id ? <Mono>{email.message_id}</Mono> : null}
                          />
                          <div style={{ marginTop: 10 }}>
                            {isContentUrl(email.content) ? (
                              <a
                                className="view-document-link"
                                href={email.content}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ fontSize: 14 }}
                              >
                                ✉️ View email content ↗
                              </a>
                            ) : (
                              <span style={{ color: '#9aa0a6', fontSize: 13 }}>
                                No stored content for this email.
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const thStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontWeight: 600,
  color: '#5f6368',
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  color: '#202124',
  verticalAlign: 'top',
};

const sectionH4: React.CSSProperties = {
  margin: '20px 0 10px',
  color: '#01B8C0',
  fontSize: '1.05rem',
  fontWeight: 700,
};

// Matches the claims hub's .audit-info-section card look.
const cardStyle: React.CSSProperties = {
  background: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderLeft: '4px solid #01B8C0',
  borderRadius: 8,
  padding: '14px 16px',
};

const pagerStyle = (disabled: boolean): React.CSSProperties => ({
  padding: '8px 20px',
  background: 'white',
  color: disabled ? '#bdc1c6' : '#01B8C0',
  border: '1px solid #ccc',
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 600,
  cursor: disabled ? 'default' : 'pointer',
});

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div
    style={{
      display: 'flex',
      gap: 12,
      padding: '7px 0',
      borderBottom: '1px solid rgba(229,231,235,0.6)',
      alignItems: 'baseline',
    }}
  >
    <dt
      style={{
        width: 140,
        flexShrink: 0,
        color: '#5f6368',
        fontWeight: 600,
        fontSize: 12,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
      }}
    >
      {label}
    </dt>
    <dd style={{ margin: 0, color: '#202124', wordBreak: 'break-word' }}>
      {value || <span style={{ color: '#9aa0a6' }}>—</span>}
    </dd>
  </div>
);

const Mono: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{children}</span>
);

export default ComplexClaimsPage;
